import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deriveDiscoveryCriteriaFromSearch,
  serializeDiscoveryCriteria,
  useDiscoveryCriteria,
} from './useDiscoveryCriteria';

/**
 * @typedef {import('../VideoCard').VideoCardProps} VideoCardProps
 */

const DEFAULT_FILTER = 'featured';
const DEFAULT_FILTERS = Object.freeze([DEFAULT_FILTER]);
const DEFAULT_ERROR_MESSAGE = 'Unable to load Twitch reels at this time.';
const FALLBACK_ERROR_MESSAGE =
  'We are having trouble refreshing Twitch reels. Showing the most recent batch available.';
const DEFAULT_RETRY_ATTEMPTS = 4;
const DEFAULT_RETRY_DELAY_MS = 500;

/**
 * Normalizes a requested filter key to one supported by the catalog.
 *
 * @param {string|undefined|null} candidate - Requested filter key.
 * @returns {string} Supported filter key.
 */
const normalizeFilterKey = (candidate, supportedFilters) => {
  const normalized = typeof candidate === 'string' ? candidate.trim().toLowerCase() : '';
  if (supportedFilters.includes(normalized)) {
    return normalized;
  }
  return supportedFilters[0] || DEFAULT_FILTER;
};

/**
 * Compares two filter lists for shallow equality.
 *
 * @param {string[]} left - First filter list.
 * @param {string[]} right - Second filter list.
 * @returns {boolean} True when lists are equivalent.
 */
const areFiltersEqual = (left, right) => {
  if (left === right) {
    return true;
  }
  if (!Array.isArray(left) || !Array.isArray(right)) {
    return false;
  }
  if (left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
};

/**
 * Normalises a raw clip payload into the shape expected by the UI.
 *
 * @param {object} clip - Raw clip payload from the API.
 * @returns {VideoCardProps|null} Normalised clip data or null when invalid.
 */
const normalizeClipPayload = (clip) => {
  if (!clip || typeof clip !== 'object') {
    return null;
  }

  const clipId =
    typeof clip.id === 'string' && clip.id.trim()
      ? clip.id.trim()
      : typeof clip.slug === 'string' && clip.slug.trim()
        ? clip.slug.trim()
        : null;

  if (!clipId) {
    return null;
  }

  const channelName =
    typeof clip.channel === 'string' && clip.channel.trim()
      ? clip.channel.trim()
      : typeof clip.broadcasterName === 'string' && clip.broadcasterName.trim()
        ? clip.broadcasterName.trim()
        : typeof clip.creatorName === 'string' && clip.creatorName.trim()
          ? clip.creatorName.trim()
          : 'Unknown channel';

  const normalizedTitle =
    typeof clip.title === 'string' && clip.title.trim()
      ? clip.title.trim()
      : `${channelName} clip`;

  const tags = Array.isArray(clip.tags)
    ? clip.tags
        .map((tag) => (typeof tag === 'string' ? tag.trim() : ''))
        .filter(Boolean)
    : [];

  const normalizedClip = {
    id: clipId,
    channel: channelName,
    creatorName:
      typeof clip.creatorName === 'string' && clip.creatorName.trim()
        ? clip.creatorName.trim()
        : null,
    avatarSrc:
      typeof clip.avatarSrc === 'string' && clip.avatarSrc.trim()
        ? clip.avatarSrc.trim()
        : null,
    title: normalizedTitle,
    embedUrl:
      typeof clip.embedUrl === 'string' && clip.embedUrl.trim()
        ? clip.embedUrl.trim()
        : null,
    twitchUrl:
      typeof clip.twitchUrl === 'string' && clip.twitchUrl.trim()
        ? clip.twitchUrl.trim()
        : null,
    tags,
    isUnavailable: Boolean(clip.isUnavailable),
    unavailableReason:
      typeof clip.unavailableReason === 'string' && clip.unavailableReason.trim()
        ? clip.unavailableReason.trim()
        : null,
  };

  if (typeof clip.viewCount === 'number') {
    normalizedClip.viewCount = clip.viewCount;
  }
  if (typeof clip.durationSeconds === 'number') {
    normalizedClip.durationSeconds = clip.durationSeconds;
  }
  if (typeof clip.language === 'string' && clip.language.trim()) {
    normalizedClip.language = clip.language.trim();
  }
  if (typeof clip.publishedAt === 'string' && clip.publishedAt.trim()) {
    normalizedClip.publishedAt = clip.publishedAt.trim();
  }

  return normalizedClip;
};

/**
 * Adds stable metadata to a clip batch so refreshes can be differentiated.
 *
 * @param {VideoCardProps[]} clips - Clip collection.
 * @param {string} filterKey - Active filter identifier.
 * @returns {VideoCardProps[]} Clips annotated with batch metadata.
 */
const decorateBatch = (clips, filterKey) => {
  if (!Array.isArray(clips) || !clips.length) {
    return [];
  }

  const batchId = `${filterKey || DEFAULT_FILTER}-${Date.now()}`;
  return clips.map((clip, index) => ({
    ...clip,
    batchId,
    order: index,
  }));
};

/**
 * Fetches reel data from the serverless API, applying lightweight validation.
 *
 * @param {string} filterKey - Requested filter.
 * @param {AbortSignal} signal - Abort handle for cancelling the load.
 * @returns {Promise<{ filter: string, filters: string[], clips: VideoCardProps[] }>}
 */
const createAbortError = () => {
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
};

const waitFor = (delayMs, signal) => {
  if (!delayMs || delayMs <= 0) {
    if (signal?.aborted) {
      throw createAbortError();
    }
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(createAbortError());
    };

    const timeoutId = setTimeout(() => {
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
      resolve();
    }, delayMs);

    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
};

const isRecoverableStatus = (status) => {
  if (typeof status !== 'number') {
    return false;
  }
  if (status === 429) {
    return true;
  }
  return status >= 500 && status < 600;
};

const requestReels = async (filterKey, signal, options = {}) => {
  const normalizedFilter =
    typeof filterKey === 'string' && filterKey.trim()
      ? filterKey.trim().toLowerCase()
      : '';

  const params = new URLSearchParams();

  if (normalizedFilter) {
    params.set('filter', normalizedFilter);
  }

  if (options?.criteria) {
    const criteriaParams = serializeDiscoveryCriteria(options.criteria);
    criteriaParams.forEach((value, key) => {
      if (typeof value === 'string' && value.trim()) {
        params.set(key, value.trim());
      }
    });
  }

  const afterCursor =
    typeof options?.pagination?.after === 'string' && options.pagination.after.trim()
      ? options.pagination.after.trim()
      : null;
  const beforeCursor =
    typeof options?.pagination?.before === 'string' && options.pagination.before.trim()
      ? options.pagination.before.trim()
      : null;

  if (afterCursor) {
    params.set('after', afterCursor);
  }

  if (beforeCursor) {
    params.set('before', beforeCursor);
  }

  const query = params.toString()
    ? `/api/twitch/clips?${params.toString()}`
    : '/api/twitch/clips';

  let response;
  try {
    response = await fetch(query, { signal });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }
    const networkError = new Error(DEFAULT_ERROR_MESSAGE);
    networkError.retryable = true;
    throw networkError;
  }

  if (!response.ok) {
    let details = '';
    try {
      details = await response.text();
    } catch (readError) {
      // Swallow errors when reading the response body for diagnostics.
    }

    const suffix = details ? `: ${details}` : '.';
    const statusError = new Error(
      `Unable to load Twitch reels (status ${response.status})${suffix}`,
    );
    statusError.status = response.status;
    statusError.retryable = isRecoverableStatus(response.status);
    throw statusError;
  }

  let payload;
  try {
    payload = await response.json();
  } catch (error) {
    throw new Error('Received invalid JSON while loading Twitch reels.');
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('Malformed Twitch reels payload.');
  }

  const availableFilters = Array.isArray(payload.filters)
    ? payload.filters
        .map((value) => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
        .filter(Boolean)
    : [];

  const normalizedFilters =
    availableFilters.length > 0 ? availableFilters : [...DEFAULT_FILTERS];

  if (!Array.isArray(payload.clips)) {
    throw new Error('Malformed Twitch reels payload: expected clips array.');
  }

  const normalizedClips = payload.clips
    .map((clip) => normalizeClipPayload(clip))
    .filter(Boolean);

  const resolvedFilter = normalizeFilterKey(payload.filter, normalizedFilters);

  const pagination = (() => {
    const rawPagination = payload.pagination && typeof payload.pagination === 'object'
      ? payload.pagination
      : null;

    if (!rawPagination) {
      return { cursor: null, pageSize: null };
    }

    const rawCursor =
      typeof rawPagination.cursor === 'string' && rawPagination.cursor.trim()
        ? rawPagination.cursor.trim()
        : null;

    const pageSizeCandidate = rawPagination.pageSize;
    const normalizedPageSize = Number.isFinite(pageSizeCandidate)
      ? pageSizeCandidate
      : typeof pageSizeCandidate === 'string' && pageSizeCandidate.trim()
        ? Number.parseInt(pageSizeCandidate.trim(), 10)
        : null;

    return {
      cursor: rawCursor,
      pageSize: Number.isFinite(normalizedPageSize) ? normalizedPageSize : null,
    };
  })();

  return {
    filter: resolvedFilter,
    filters: normalizedFilters,
    clips: normalizedClips,
    pagination,
  };
};

/**
 * Loads reels with retry logic for recoverable errors.
 *
 * @param {string} filterKey - Requested filter.
 * @param {AbortSignal} signal - Abort handle for cancelling the load.
 * @param {{ maxAttempts?: number, baseDelay?: number }} [options]
 * @returns {Promise<{ filter: string, filters: string[], clips: VideoCardProps[] }>}
 */
export const loadClips = async (
  filterKey,
  signal,
  options = {},
) => {
  const paginationOptions = options?.pagination;
  const criteriaOptions = options?.criteria;
  const maxAttempts = Math.max(
    1,
    Number.isInteger(options.maxAttempts)
      ? options.maxAttempts
      : DEFAULT_RETRY_ATTEMPTS,
  );
  const baseDelay =
    typeof options.baseDelay === 'number' && options.baseDelay >= 0
      ? options.baseDelay
      : DEFAULT_RETRY_DELAY_MS;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      throw createAbortError();
    }

    try {
      return await requestReels(filterKey, signal, {
        pagination: paginationOptions,
        criteria: criteriaOptions,
      });
    } catch (error) {
      if (error?.name === 'AbortError' || signal?.aborted) {
        throw error;
      }

      const status = typeof error?.status === 'number' ? error.status : null;
      const retryable = Boolean(error?.retryable) || isRecoverableStatus(status);

      if (!retryable || attempt === maxAttempts - 1) {
        const finalError =
          error instanceof Error ? error : new Error(DEFAULT_ERROR_MESSAGE);
        throw finalError;
      }

      const delay = baseDelay * 2 ** attempt;
      console.warn(
        `[useReelFeed] Recoverable error while loading Twitch reels (attempt ${
          attempt + 2
        }/${maxAttempts}): ${error?.message || error}`,
      );
      await waitFor(delay, signal);
    }
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

/**
 * Lightweight feed hook that always returns the embedded Twitch clip used for testing.
 *
 * @param {string} [initialFilter=DEFAULT_FILTER] - Requested filter key.
 * @returns {{
 *   reels: VideoCardProps[];
 *   filter: string;
 *   filters: string[];
 *   status: 'idle'|'loading'|'ready'|'error';
 *   error: Error|null;
 *   refresh: () => Promise<void>;
 *   applyFilter: (nextFilter: string) => Promise<void>;
 *   loadMore: () => Promise<unknown>;
 *   hasMore: boolean;
 *   isLoadingMore: boolean;
 *   loadMoreError: Error|null;
 * }}
 */
export function useReelFeed(initialFilter = DEFAULT_FILTER) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(() => DEFAULT_FILTERS);
  const [filter, setFilter] = useState(() =>
    normalizeFilterKey(initialFilter, DEFAULT_FILTERS),
  );
  const initialCriteriaRef = useRef();

  if (initialCriteriaRef.current === undefined) {
    initialCriteriaRef.current = (() => {
      if (typeof window === 'undefined') {
        return undefined;
      }

      const { location } = window;
      if (!location || typeof location.search !== 'string') {
        return undefined;
      }

      return deriveDiscoveryCriteriaFromSearch(location.search) ?? undefined;
    })();
  }
  const {
    criteria,
    setBroadcaster,
    setCategory,
    setTags,
    toggleTag,
    clearCriteria,
    serializeCriteria,
    criteriaSignature,
  } = useDiscoveryCriteria(initialCriteriaRef.current);
  const latestRequestedFilterRef = useRef(filter);
  const cachedReelsRef = useRef([]);
  const clipIdsRef = useRef(new Set());
  const requestedCursorsRef = useRef(new Set());
  const loadMorePromiseRef = useRef(null);
  const loadMoreControllerRef = useRef(null);

  const [clipState, setClipState] = useState(() => ({
    reels: [],
    cursor: null,
    pageSize: null,
  }));
  const [loadMoreState, setLoadMoreState] = useState(() => ({
    status: 'idle',
    error: null,
  }));

  const queryKey = useMemo(
    () => ['reels', filter, criteriaSignature],
    [criteriaSignature, filter],
  );

  const {
    data,
    error: queryError,
    status: queryStatus,
    fetchStatus,
  } = useQuery({
    queryKey,
    queryFn: ({ signal }) => loadClips(filter, signal, { criteria }),
    keepPreviousData: true,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const nextFilters = data.filters.length ? data.filters : DEFAULT_FILTERS;
    setFilters((prevFilters) =>
      areFiltersEqual(prevFilters, nextFilters) ? prevFilters : nextFilters,
    );

    const resolvedFilter = normalizeFilterKey(data.filter, nextFilters);
    setFilter((prevFilter) =>
      prevFilter === resolvedFilter ? prevFilter : resolvedFilter,
    );
  }, [data]);

  useEffect(() => {
    latestRequestedFilterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    if (queryStatus !== 'success' || !data) {
      return;
    }

    const sanitizedCursor =
      typeof data.pagination?.cursor === 'string' && data.pagination.cursor.trim()
        ? data.pagination.cursor.trim()
        : null;

    const sanitizedPageSize = (() => {
      const candidate = data.pagination?.pageSize;
      if (Number.isFinite(candidate)) {
        return candidate;
      }
      if (typeof candidate === 'string' && candidate.trim()) {
        const parsed = Number.parseInt(candidate.trim(), 10);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    })();

    const decorated = decorateBatch(data.clips, filter);

    clipIdsRef.current = new Set(decorated.map((clip) => clip.id));
    requestedCursorsRef.current = new Set();

    setClipState({
      reels: decorated,
      cursor: sanitizedCursor,
      pageSize: sanitizedPageSize ?? null,
    });

    setLoadMoreState((prev) =>
      prev.status === 'idle' && !prev.error ? prev : { status: 'idle', error: null },
    );
  }, [data, filter, queryStatus]);

  useEffect(() => {
    cachedReelsRef.current = clipState.reels;
  }, [clipState.reels]);

  useEffect(() => {
    return () => {
      if (loadMoreControllerRef.current) {
        loadMoreControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (loadMoreControllerRef.current) {
      loadMoreControllerRef.current.abort();
      loadMoreControllerRef.current = null;
    }
    loadMorePromiseRef.current = null;
    requestedCursorsRef.current = new Set();
    setClipState((prev) => (prev.cursor === null ? prev : { ...prev, cursor: null }));
    setLoadMoreState((prev) =>
      prev.status === 'idle' && !prev.error ? prev : { status: 'idle', error: null },
    );
  }, [criteriaSignature, filter]);

  const isLoading =
    fetchStatus === 'fetching' ||
    queryStatus === 'pending' ||
    queryStatus === 'loading';

  const status = isLoading
    ? 'loading'
    : queryStatus === 'error'
      ? 'error'
      : queryStatus === 'success'
        ? 'ready'
        : 'idle';

  const queryFailed = queryStatus === 'error';
  const fallbackActive = queryFailed && cachedReelsRef.current.length > 0;
  const reels = fallbackActive ? cachedReelsRef.current : clipState.reels;

  const baseError =
    queryError instanceof Error
      ? queryError
      : queryError
        ? new Error(DEFAULT_ERROR_MESSAGE)
        : null;

  const error = fallbackActive
    ? (() => {
        const friendlyError = new Error(FALLBACK_ERROR_MESSAGE);
        if (baseError) {
          friendlyError.cause = baseError;
      }
      return friendlyError;
    })()
    : baseError;

  const loadMore = useCallback(() => {
    if (fallbackActive) {
      return Promise.resolve(null);
    }

    const nextCursor =
      typeof clipState.cursor === 'string' && clipState.cursor.trim()
        ? clipState.cursor.trim()
        : null;

    if (!nextCursor) {
      return Promise.resolve(null);
    }

    if (loadMorePromiseRef.current) {
      return loadMorePromiseRef.current;
    }

    if (requestedCursorsRef.current.has(nextCursor)) {
      return Promise.resolve(null);
    }

    requestedCursorsRef.current.add(nextCursor);

    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    setLoadMoreState({ status: 'loading', error: null });

    const request = loadClips(filter, controller.signal, {
      pagination: { after: nextCursor },
      criteria,
    })
      .then((result) => {
        if (!result || !Array.isArray(result.clips)) {
          throw new Error(DEFAULT_ERROR_MESSAGE);
        }

        const freshClips = result.clips.filter((clip) => {
          if (!clip || typeof clip !== 'object') {
            return false;
          }
          const clipId = typeof clip.id === 'string' && clip.id.trim()
            ? clip.id.trim()
            : null;
          if (!clipId) {
            return false;
          }
          if (clipIdsRef.current.has(clipId)) {
            return false;
          }
          return true;
        });

        if (freshClips.length) {
          freshClips.forEach((clip) => {
            if (typeof clip.id === 'string' && clip.id.trim()) {
              clipIdsRef.current.add(clip.id.trim());
            }
          });
        }

        const decoratedFresh = decorateBatch(freshClips, filter);

        setClipState((prev) => {
          const nextCursorValue =
            typeof result.pagination?.cursor === 'string' && result.pagination.cursor.trim()
              ? result.pagination.cursor.trim()
              : null;

          const nextPageSize = (() => {
            const candidate = result.pagination?.pageSize;
            if (Number.isFinite(candidate)) {
              return candidate;
            }
            if (typeof candidate === 'string' && candidate.trim()) {
              const parsed = Number.parseInt(candidate.trim(), 10);
              return Number.isFinite(parsed) ? parsed : prev.pageSize;
            }
            return prev.pageSize;
          })();

          if (!decoratedFresh.length) {
            if (prev.cursor === nextCursorValue && prev.pageSize === nextPageSize) {
              return prev;
            }
            return {
              reels: prev.reels,
              cursor: nextCursorValue,
              pageSize: nextPageSize,
            };
          }

          return {
            reels: [...prev.reels, ...decoratedFresh],
            cursor: nextCursorValue,
            pageSize: nextPageSize,
          };
        });

        setLoadMoreState({ status: 'idle', error: null });
        return result;
      })
      .catch((error) => {
        requestedCursorsRef.current.delete(nextCursor);

        if (error?.name === 'AbortError') {
          setLoadMoreState((prevState) =>
            prevState.status === 'loading'
              ? { status: 'idle', error: prevState.error }
              : prevState,
          );
          throw error;
        }

        const normalizedError =
          error instanceof Error ? error : new Error(DEFAULT_ERROR_MESSAGE);
        setLoadMoreState({ status: 'error', error: normalizedError });
        throw normalizedError;
      })
      .finally(() => {
        loadMorePromiseRef.current = null;
        loadMoreControllerRef.current = null;
      });

    loadMorePromiseRef.current = request;
    return request;
  }, [clipState.cursor, criteria, criteriaSignature, fallbackActive, filter]);

  const hasMore =
    !fallbackActive &&
    typeof clipState.cursor === 'string' &&
    clipState.cursor.trim().length > 0 &&
    !requestedCursorsRef.current.has(clipState.cursor);

  const isLoadingMore = loadMoreState.status === 'loading';
  const loadMoreError = loadMoreState.error instanceof Error ? loadMoreState.error : null;

  const refresh = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey, exact: true });
  }, [queryClient, queryKey]);

  const applyFilter = useCallback(
    async (nextFilter) => {
      const supportedFilters = filters.length ? filters : DEFAULT_FILTERS;
      const normalized = normalizeFilterKey(nextFilter, supportedFilters);

      if (normalized === filter) {
        return;
      }

      latestRequestedFilterRef.current = normalized;

      await queryClient.prefetchQuery({
        queryKey: ['reels', normalized, criteriaSignature],
        queryFn: ({ signal }) => loadClips(normalized, signal, { criteria }),
        retry: false,
      });

      if (latestRequestedFilterRef.current === normalized) {
        setFilter(normalized);
      }
    },
    [criteria, criteriaSignature, filter, filters, queryClient],
  );

  return {
    reels,
    filter,
    filters,
    criteria,
    status,
    error,
    refresh,
    applyFilter,
    loadMore,
    hasMore,
    isLoadingMore,
    loadMoreError,
    setBroadcaster,
    setCategory,
    setTags,
    toggleTag,
    clearCriteria,
    serializeCriteria,
    criteriaSignature,
  };
}
